import type { AuditFilter } from '@speles7172/audit-client/core';
import type { AuditFilterOptions, AuditListResult, AuditRecord } from '@peles/shared';
import { apiClient } from './client';

export async function getEntityAudit(entityType: string, entityId: string): Promise<AuditRecord[]> {
  const { data } = await apiClient.get<AuditRecord[]>(`/audit/${entityType}/${entityId}`);
  return data;
}

/**
 * The audit log list.
 *
 * The parameter names are snake_case because the endpoint parses them with the
 * package's `auditFilterFromQuery`, and that is the vocabulary it reads.
 * Keeping to it means a link the console builds and a URL someone pastes reach
 * the server the same way.
 */
export async function listAuditLogs(filters: AuditFilter): Promise<AuditListResult> {
  const params: Record<string, string | number> = {};
  if (filters.actorIds?.length) params.actor_id = filters.actorIds.join(',');
  if (filters.actions?.length) params.action = filters.actions.join(',');
  if (filters.entityTypes?.length) params.entity_type = filters.entityTypes.join(',');
  if (filters.entityId) params.entity_id = filters.entityId;
  if (filters.search) params.search = filters.search;
  // Its own parameter: `search` matches the entity name and the message, so an
  // address sent through it finds nothing.
  if (filters.ipAddress) params.ip_address = filters.ipAddress;
  // The package's filter allows a Date or an epoch as well as an ISO string;
  // the wire only carries the string.
  if (filters.from !== undefined) params.from = asIso(filters.from);
  if (filters.to !== undefined) params.to = asIso(filters.to);
  if (filters.limit !== undefined) params.limit = filters.limit;
  if (filters.offset !== undefined) params.offset = filters.offset;
  if (filters.order) params.order = filters.order;
  if (filters.sort) params.sort = filters.sort;

  const { data } = await apiClient.get<AuditListResult>('/audit', { params });
  return data;
}

function asIso(value: string | Date | number): string {
  return value instanceof Date ? value.toISOString() : String(value);
}

export async function getAuditFilterOptions(): Promise<AuditFilterOptions> {
  const { data } = await apiClient.get<AuditFilterOptions>('/audit/filter-options');
  return data;
}
